import { Dispatch, SetStateAction } from 'react'

import { StoreObject, useMutation } from '@apollo/client'

import {
  CreateManualTransactionMutation,
  CreateManualTransactionMutationVariables,
  DeleteManualTransactionMutation,
  DeleteManualTransactionMutationVariables,
  ManualPaymentRecord,
  OrderRemainingBalanceDocument,
  UpdateManualTransactionMutation,
  UpdateManualTransactionMutationVariables,
} from '__generated__/atlas-types'

import { atlasClient } from 'graphql/client'
import { useRenderToasters } from 'hooks/useRenderToasters'

import {
  CREATE_MANUAL_TRANSACTION_MUTATION,
  DELETE_MANUAL_TRANSACTION_MUTATION,
  UPDATE_MANUAL_TRANSACTION_MUTATION,
} from './ManualTransactions.mutations'
import { MANUAL_TRANSACTION_FRAGMENT } from './ManualTransactions.queries'

const useManualTransactionCRUD = (
  setIsModalOpen: Dispatch<SetStateAction<boolean>>,
  id?: string,
) => {
  const { renderSuccessToast } = useRenderToasters()
  const [createManualTransaction] = useMutation<
    CreateManualTransactionMutation,
    CreateManualTransactionMutationVariables
  >(CREATE_MANUAL_TRANSACTION_MUTATION, {
    onCompleted: () => {
      renderSuccessToast({
        title: 'Manual Transaction Created',
        description:
          'You have successfully added a record of a transaction made outside of JOOR Pay.',
      })
      setIsModalOpen(false)
    },
    update: (cache, { data }) => {
      if (!data?.createManualPaymentRecord) return
      cache.modify({
        fields: {
          manualPaymentRecords(existingRecords = []) {
            const newRecordRef = cache.writeFragment({
              data: data.createManualPaymentRecord,
              fragment: MANUAL_TRANSACTION_FRAGMENT,
            })
            return [...existingRecords, newRecordRef]
          },
        },
      })
    },
    client: atlasClient,
    refetchQueries: [OrderRemainingBalanceDocument],
  })

  const [updateManualTransaction] = useMutation<
    UpdateManualTransactionMutation,
    UpdateManualTransactionMutationVariables
  >(UPDATE_MANUAL_TRANSACTION_MUTATION, {
    onCompleted: () => {
      renderSuccessToast({
        title: 'Manual Transaction Updated',
        description: 'You have successfully updated the manual transaction',
      })
      setIsModalOpen(false)
    },
    client: atlasClient,
    refetchQueries: [OrderRemainingBalanceDocument],
  })

  const [deleteManualTransaction] = useMutation<
    DeleteManualTransactionMutation,
    DeleteManualTransactionMutationVariables
  >(DELETE_MANUAL_TRANSACTION_MUTATION, {
    onCompleted: () => {
      renderSuccessToast({
        title: 'Manual Transaction Deleted',
        description: 'You have successfully deleted the manual transaction',
      })
      setIsModalOpen(false)
    },
    update: (cache, { data }) => {
      if (!data?.deleteManualPaymentRecord) return
      const existingRecord = cache.readFragment<ManualPaymentRecord>({
        id: `ManualPaymentRecord:${id}`,
        fragment: MANUAL_TRANSACTION_FRAGMENT,
      })
      if (!existingRecord) return
      cache.evict({
        id: cache.identify(existingRecord as unknown as StoreObject),
      })
      cache.gc()
    },
    client: atlasClient,
    refetchQueries: [OrderRemainingBalanceDocument],
  })
  return {
    createManualTransaction,
    updateManualTransaction,
    deleteManualTransaction,
  }
}

export default useManualTransactionCRUD
